import ExtensionsAPI from 'dashboard/api/asterisk/extensions';
import { createHandleRealtimeEvent } from './_helpers/asteriskRealtime';
import { createPendingOp } from './_helpers/asteriskAsyncOp';

const RESOURCE = 'extension';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getRecords: $state => $state.records,
  getUIFlags: $state => $state.uiFlags,
  getByExtension: $state => extension =>
    $state.records.find(
      r => r.extension === extension || r.extension === String(extension)
    ),
  getById: $state => id =>
    $state.records.find(
      r => r.id === id || r.extension === id || r.extension === String(id)
    ),
  getActiveExtensions: $state =>
    $state.records.filter(r => r.active === 1 || r.active === true),
};

export const actions = {
  async fetch({ commit }) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await ExtensionsAPI.get();
      const extensions = response.data.extensions || [];
      commit('SET_RECORDS', extensions);
      return extensions;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async show(_, id) {
    const response = await ExtensionsAPI.show(id);
    return response.data.extension;
  },

  // Backend retorna 202 (job enfileirado). A action resolve a promise apenas
  // quando o broadcast `asterisk.extension.changed` confirma (success ou error).
  // Permite que o form use `toast.promise()` mostrando loading real.
  async create({ commit }, { data, agentId }) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const payload = { extension: { ...data } };
      if (agentId) payload.extension.agent_id = agentId;
      await ExtensionsAPI.create(payload);
      await createPendingOp(RESOURCE, 'created', data.extension);
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit }, { id, data, agentId }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const payload = { extension: { ...data } };
      if (agentId) payload.extension.agent_id = agentId;
      await ExtensionsAPI.update(id, payload);
      await createPendingOp(RESOURCE, 'updated', id);
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await ExtensionsAPI.delete(id);
      await createPendingOp(RESOURCE, 'deleted', id);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  // Recebe broadcast ActionCable disparado pelo Create/Update/DeleteExtensionJob
  // após o ACI processar a request enfileirada. Vide actionCable.js.
  handleRealtimeEvent: createHandleRealtimeEvent('fetch', RESOURCE),

  async toggleRecord({ commit, state: moduleState }, extensionId) {
    const extension = moduleState.records.find(
      r => r.extension === extensionId || r.extension === String(extensionId)
    );

    if (!extension) return undefined;

    const currentRecord = extension.record === 1 || extension.record === true;
    const newRecord = currentRecord ? 0 : 1;

    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      // Enviar dados suficientes para o backend reconciliar
      // AccountUser.sip_extension sem depender de GET à API externa.
      const payload = {
        extension: {
          record: newRecord,
          extension: extension.extension,
          caller_id_name: extension.caller_id_name,
        },
      };
      await ExtensionsAPI.update(extensionId, payload);

      // Optimistic update - only update the record field, don't use API response
      // because the Asterisk API doesn't return the full extension data on update
      commit('UPDATE_RECORD', { id: extensionId, data: { record: newRecord } });

      return { ...extension, record: newRecord };
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },
};

export const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_RECORDS($state, records) {
    $state.records = records;
  },

  ADD_RECORD($state, record) {
    $state.records.push(record);
  },

  UPDATE_RECORD($state, { id, data }) {
    const index = $state.records.findIndex(
      r => r.id === id || r.extension === id || r.extension === String(id)
    );
    if (index !== -1) {
      // Use splice to ensure Vue reactivity
      $state.records.splice(index, 1, { ...$state.records[index], ...data });
    }
  },

  DELETE_RECORD($state, id) {
    $state.records = $state.records.filter(
      r => r.id !== id && r.extension !== id && r.extension !== String(id)
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
